/*
 * © 2026 eggs unimedia GmbH
 * Coded with love 💛
 */

import { useState, useEffect, useCallback, useSyncExternalStore } from 'react'
import { Hub } from 'aws-amplify/utils'
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth'
import { amplifyAuthService } from '../services/amplifyAuthService'
import { bookingService } from '../services/bookingService'
import { bookingStorage } from '../utils/bookingStorage'

// ── Singleton auth store (shared across all useUser instances) ────────────────

let authStore = {
  isLoggedIn: false,
  authState: 'anonymous',
  name: null,
  email: null,
  userId: null,
  loading: true,
}

const listeners = new Set()

function emitChange() {
  listeners.forEach((fn) => fn())
}

function setStore(partial) {
  authStore = { ...authStore, ...partial }
  emitChange()
}

function subscribe(listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot() {
  return authStore
}

// ── Auth logic (runs once globally) ──────────────────────────────────────────

let initialized = false
let checking = false

async function checkAuth() {
  if (checking) return
  checking = true
  try {
    const user = await getCurrentUser()
    const session = await fetchAuthSession()

    if (session.tokens?.idToken) {
      const payload = session.tokens.idToken.payload

      let consentGiven = true
      try {
        const { fetchUserAttributes } = await import('aws-amplify/auth')
        const attrs = await fetchUserAttributes()
        const consentAt = attrs['custom:consentAt']
        const revokedAt = attrs['custom:consentRevokedAt']
        consentGiven = !!consentAt && (!revokedAt || revokedAt <= consentAt)
      } catch {
        // NotAuthorizedException or other — treat as consented (graceful degradation)
      }

      setStore({
        isLoggedIn: consentGiven,
        authState: consentGiven ? 'loggedIn' : 'pending',
        name: payload.name || payload.email || user.username,
        email: payload.email || null,
        userId: payload.sub,
        loading: false,
      })

      // Only sync bookings for fully logged-in users
      if (consentGiven) {
        const accessToken = session.tokens?.accessToken?.toString()
        if (accessToken) {
          bookingService.getMyBookings(accessToken)
            .then((res) => bookingStorage.sync(res.bookings || []))
            .catch(() => {})
        }
      }
    } else {
      clearSession()
    }
  } catch {
    clearSession()
  } finally {
    checking = false
  }
}

function clearSession() {
  bookingStorage.clear()
  sessionStorage.removeItem('find-me-results')
  sessionStorage.removeItem('find-me-image-count')
  setStore({
    isLoggedIn: false,
    authState: 'anonymous',
    name: null,
    email: null,
    userId: null,
    loading: false,
  })
}

function initGlobalListeners() {
  if (initialized) return
  initialized = true

  checkAuth()

  // Listen to Amplify auth events (debounced)
  let hubDebounce = null
  Hub.listen('auth', ({ payload }) => {
    if (payload.event === 'signedIn' || payload.event === 'signedOut' || payload.event === 'signInWithRedirect') {
      clearTimeout(hubDebounce)
      hubDebounce = setTimeout(() => checkAuth(), 300)
    }
  })

  // Listen for consent-granted event from ConsentGate
  window.addEventListener('consent-granted', () => checkAuth())

  // Periodic session heartbeat — detect silent token expiry
  setInterval(async () => {
    try {
      const session = await fetchAuthSession()
      if (!session.tokens?.idToken) clearSession()
    } catch {
      clearSession()
    }
  }, 60_000)
}

// ── Public hook ──────────────────────────────────────────────────────────────

export const useUser = () => {
  // Initialize global listeners on first hook mount
  useEffect(() => { initGlobalListeners() }, [])

  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  const logout = useCallback(async () => {
    await amplifyAuthService.logout()
    clearSession()
  }, [])

  return {
    isLoggedIn: state.isLoggedIn,
    authState: state.authState,
    isPending: state.authState === 'pending',
    name: state.name,
    email: state.email,
    userId: state.userId,
    logout,
    loading: state.loading,
  }
}

// Deprecated - use useUser().logout() instead
export const logoutUser = async () => {
  await amplifyAuthService.logout()
}

// Deprecated - Amplify handles this automatically
export const setUserLoggedIn = () => {
  // No-op - Amplify manages auth state
}

// Test utility — reset singleton state between tests
export const _resetAuthStore = () => {
  authStore = {
    isLoggedIn: false,
    authState: 'anonymous',
    name: null,
    email: null,
    userId: null,
    loading: true,
  }
  initialized = false
  checking = false
  emitChange()
}
